import { createContext, useContext, useEffect, useState } from "react";
import { EN, PATTERNS } from "./dictionary";

const LangCtx = createContext({ lang: "de", setLang: () => {} });
export const useLang = () => useContext(LangCtx);

const originals = new WeakMap();
const ATTRS = ["placeholder", "title", "aria-label"];

function translate(text) {
  const trimmed = text.trim();
  if (!trimmed) return null;
  let out = EN[trimmed];
  if (out == null) {
    for (const [re, rep] of PATTERNS) {
      if (re.test(trimmed)) { out = trimmed.replace(re, rep); break; }
    }
  }
  if (out == null || out === trimmed) return null;
  const lead = text.match(/^\s*/)[0], trail = text.match(/\s*$/)[0];
  return lead + out + trail;
}

function applyNode(node, lang) {
  if (node.nodeType === Node.TEXT_NODE) {
    const p = node.parentElement;
    if (!p || ["SCRIPT", "STYLE"].includes(p.tagName)) return;
    if (lang === "en") {
      const src = originals.has(node) ? originals.get(node) : node.nodeValue;
      const t = translate(src);
      if (t != null) { if (!originals.has(node)) originals.set(node, src); if (node.nodeValue !== t) node.nodeValue = t; }
    } else if (originals.has(node)) {
      node.nodeValue = originals.get(node); originals.delete(node);
    }
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  for (const a of ATTRS) {
    if (!node.hasAttribute(a)) continue;
    const key = `__i18n_${a}`;
    if (lang === "en") {
      const src = node[key] ?? node.getAttribute(a);
      const t = translate(src);
      if (t != null) { node[key] = src; node.setAttribute(a, t); }
    } else if (node[key] != null) { node.setAttribute(a, node[key]); delete node[key]; }
  }
  if (node.tagName === "OPTION" && node.childNodes.length === 0 && node.label) return;
  node.childNodes.forEach((c) => applyNode(c, lang));
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("eb_lang") || "de");
  const setLang = (l) => { localStorage.setItem("eb_lang", l); setLangState(l); };

  useEffect(() => {
    document.documentElement.lang = lang;
    applyNode(document.body, lang);
    if (lang !== "en") return;
    let scheduled = false;
    const obs = new MutationObserver((muts) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        obs.disconnect();
        for (const m of muts) {
          if (m.type === "characterData") applyNode(m.target, "en");
          else m.addedNodes.forEach((n) => applyNode(n, "en"));
        }
        obs.observe(document.body, { childList: true, subtree: true, characterData: true });
      });
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [lang]);

  return <LangCtx.Provider value={{ lang, setLang }}>{children}</LangCtx.Provider>;
}

export const LanguageToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-lg border border-slate-800 p-0.5 text-xs font-semibold" data-testid="lang-toggle">
      {["de", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`} translate="no"
          className={`px-2 py-1 rounded-md uppercase transition-colors ${lang === l ? "bg-[#FF5A1F] text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
};
